import {Card} from 'platform/components';
import {Box, HStack, Show, Text, type ThemeColorPath, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {isNil} from 'ramda';

import {useGetServiceOrderTypesQuery} from '@dms/api/workshop';
import {CHART_PALETTE} from '@dms/features/report-charts';
import i18n from '@dms/i18n';
import {getQuantityWithHandlingUnit} from '@dms/warehouse/shared';

import type {ArticleIssueChannel} from '@prompt-bridge/shared/validations';

import {EMPTY_PLACEHOLDER} from 'shared/web';

// Fallback labels for a type that the service order types of the tenant do not name.
const CHANNEL_LABELS = {
  COM: 'page.vehicle.financingAndLeasing.commercial',
  INS: 'entity.order.labels.insurance',
  CON: 'aiAssistant.businessCase.recap.states.CONTRACT',
  WAR: 'page.salesSettings.labels.warranty',
  INT: 'entity.checkout.labels.internal',
  CLA: 'entity.order.labels.claim',
  DIRECT: 'entity.warehouse.labels.directSale',
  OTHER: 'entity.warehouse.labels.other',
} as const;

// A direct sale has no service order type, so it forms its own group next to the issues to
// orders. Every other channel is a service order type, `OTHER` included: the type of such an
// order is missing or none of the six.
const DIRECT_SALE_CHANNEL = 'DIRECT' satisfies ArticleIssueChannel['channel'];

// A service order type keeps the colour of the colour stripe of its service order card, so a
// reader who knows warranty orders as orange reads this breakdown the same way. Commercial orders
// and direct sales keep the first chart palette colour, blue 60. An order without a known type is
// grey, like the neutral stripe. A channel with more returns than issues keeps its colour; its
// bar grows from the right.
const CHANNEL_COLORS = {
  COM: CHART_PALETTE.blue.base,
  INS: 'palettes.magenta.60.100',
  CON: 'palettes.green.60.100',
  WAR: 'palettes.orange.60.100',
  INT: 'palettes.teal.60.100',
  CLA: 'palettes.red.60.100',
  DIRECT: CHART_PALETTE.blue.base,
  OTHER: 'palettes.neutral.300.100',
} as const satisfies Record<ArticleIssueChannel['channel'], ThemeColorPath>;

// A group is unknown when one of its channels is unknown, the same way as the total.
const sumOf = (channels: ArticleIssueChannel[], key: 'netQuantity' | 'sharePercent') =>
  channels.some((channel) => isNil(channel[key]))
    ? null
    : channels.reduce((sum, channel) => sum + (channel[key] ?? 0), 0);

interface ChannelBarProps {
  value: number | null;
  /** The largest absolute value of all bars, so that every bar uses the same scale. */
  maximum: number;
  color: ThemeColorPath;
}

function ChannelBar(props: ChannelBarProps) {
  const isNegative = (props.value ?? 0) < 0;
  return (
    <Box width="100%" height={3} backgroundColor="palettes.neutral.10.100" borderRadius="small">
      <HStack justify={isNegative ? 'flex-end' : 'flex-start'}>
        <Box
          width={
            props.maximum === 0 ? '0%' : `${(Math.abs(props.value ?? 0) / props.maximum) * 100}%`
          }
          height={3}
          backgroundColor={props.color}
          borderRadius="small"
        />
      </HStack>
    </Box>
  );
}

interface StatisticsChannelsProps {
  channels: ArticleIssueChannel[];
  handlingUnitLabel: string;
  historicalBasis: string;
}

export function StatisticsChannels(props: StatisticsChannelsProps) {
  const formatNumber = useFormatNumber();
  // The tenant names its service order types, so a type shows the same name as its orders. Without
  // access to the types, the fallback label stays.
  const {data: serviceOrderTypes} = useGetServiceOrderTypesQuery();
  const labelOf = (channel: ArticleIssueChannel) =>
    serviceOrderTypes?.find((type) => type?.code === channel.channel)?.name ??
    i18n.t(CHANNEL_LABELS[channel.channel]);
  const isComplete = props.channels.every((channel) => channel.netQuantity !== null);
  const hasIssues = props.channels.some((channel) => (channel.netQuantity ?? 0) !== 0);
  const groups = [
    {
      id: 'orders',
      label: i18n.t('entity.warehouse.reporting.issuesToOrders'),
      channels: props.channels.filter((channel) => channel.channel !== DIRECT_SALE_CHANNEL),
    },
    {
      id: 'directSales',
      label: i18n.t('entity.warehouse.labels.directSales'),
      channels: props.channels.filter((channel) => channel.channel === DIRECT_SALE_CHANNEL),
    },
  ];
  const maximum = Math.max(
    0,
    ...props.channels.map((channel) => Math.abs(channel.netQuantity ?? 0))
  );
  const formatQuantity = (value: number | null) =>
    isNil(value)
      ? EMPTY_PLACEHOLDER
      : getQuantityWithHandlingUnit(
          formatNumber(value, 2) ?? EMPTY_PLACEHOLDER,
          props.handlingUnitLabel
        );
  // A share is the part of all issues, so the shares of both groups add up to 100 %.
  const formatPercent = (value: number | null) =>
    isNil(value) ? EMPTY_PLACEHOLDER : (formatNumber(value, 1) ?? EMPTY_PLACEHOLDER) + ' %';

  return (
    <Card
      title={i18n.t('entity.warehouse.reporting.issuesBreakdown')}
      parameters={[
        props.historicalBasis,
        {
          title: `${i18n.t('entity.warehouse.reporting.annualPartUsage')}: ${formatQuantity(sumOf(props.channels, 'netQuantity'))}`,
          tooltip: i18n.t('entity.warehouse.reporting.annualPartUsageTooltip'),
          shouldShowTooltipIcon: true,
        },
      ]}
    >
      <VStack spacing={4}>
        <Show when={!isComplete}>
          <Text>{i18n.t('entity.warehouse.reporting.inventoryTurnoverIncompleteSeries')}</Text>
        </Show>
        <Show when={isComplete && !hasIssues}>
          <Text>{i18n.t('entity.warehouse.reporting.noIssueHistory')}</Text>
        </Show>
        <Show when={hasIssues}>
          <VStack spacing={6}>
            {groups.map((group) => {
              const visibleChannels = group.channels.filter((channel) => channel.netQuantity !== 0);
              // A group of one channel, the direct sale, names it in its heading already, so its
              // bar follows the heading without a row of its own.
              const isSingleChannel = group.channels.length === 1;
              return (
                <VStack key={group.id} spacing={3}>
                  <HStack spacing={3} justify="space-between" align="center" wrap>
                    <Text alternative>{group.label}</Text>
                    <HStack spacing={3} align="center">
                      <Text alternative>
                        {formatQuantity(sumOf(group.channels, 'netQuantity'))}
                      </Text>
                      <Text size="small" color="secondary">
                        {formatPercent(sumOf(group.channels, 'sharePercent'))}
                      </Text>
                    </HStack>
                  </HStack>
                  {visibleChannels.map((channel) =>
                    isSingleChannel ? (
                      <ChannelBar
                        key={channel.channel}
                        value={channel.netQuantity}
                        maximum={maximum}
                        color={CHANNEL_COLORS[channel.channel]}
                      />
                    ) : (
                      <VStack key={channel.channel} spacing={1}>
                        <HStack spacing={3} justify="space-between" align="center" wrap>
                          <Text size="small">{labelOf(channel)}</Text>
                          <HStack spacing={3} align="center">
                            <Text size="small" alternative>
                              {formatQuantity(channel.netQuantity)}
                            </Text>
                            <Text size="xSmall" color="secondary">
                              {formatPercent(channel.sharePercent)}
                            </Text>
                          </HStack>
                        </HStack>
                        <ChannelBar
                          value={channel.netQuantity}
                          maximum={maximum}
                          color={CHANNEL_COLORS[channel.channel]}
                        />
                      </VStack>
                    )
                  )}
                </VStack>
              );
            })}
          </VStack>
        </Show>
      </VStack>
    </Card>
  );
}
